/**
 * State a new test case, plan or campaign (issue #106).
 *
 * Ordered the way the decision is made: which KIND first, because everything
 * below follows from it - the types on offer, the parents that make sense (a
 * case lives in a plan, a plan in a campaign), and the fields. The generic
 * dialog offered one list of fields for all three, which is how a campaign came
 * to be asked for a case's test level (issue #85).
 *
 * The kind's main fields are asked up front and the optional ones sit behind
 * "More fields", so a case that only needs a requirement and an objective is not
 * a screenful. What a kind HAS is the server's answer (`types[].fields`); this
 * only decides which of them come first.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import FieldControl from './FieldControl'
import { ASKED_ELSEWHERE, GROUP_LABEL, LONG_TEXT_FIELDS, MAIN_FIELDS, PARENT_GROUP } from './verification-model'
import type { VerificationGroup } from './verification-model'
import { ApiError, api } from '../../api'
import { useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { useUnsavedGuard } from '../../state/Unsaved'
import { MONOSPACE } from '../../theme'
import type { KindSchema, PackageRow, VerificationRow } from '../../schema'
import ElementPicker from '../common/ElementPicker'

const GROUPS: VerificationGroup[] = ['case', 'plan', 'campaign']

/** A case's pinning to a test level only means something on a leveled type. */
const LEVELED_ONLY = new Set(['test_level', 'stand', 'location'])

export default function CreateVerificationDialog({
  kind,
  pkg = '',
  group: fixedGroup,
  parent: fixedParent,
  onClose,
  onCreated,
}: {
  kind: KindSchema
  /** The package it goes in, when somebody has already chosen one. */
  pkg?: string
  /** Start on this kind and do not offer the others - a plan's "Add case". */
  group?: VerificationGroup
  /** Declare it inside this plan or campaign, fixed. */
  parent?: { qualifiedName: string; label: string }
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const schema = useSchema()
  const toasts = useToasts()

  const [group, setGroup] = useState<VerificationGroup>(fixedGroup ?? 'case')
  const [typeName, setTypeName] = useState('')
  const [parent, setParent] = useState(fixedParent?.qualifiedName ?? '')
  const [packages, setPackages] = useState<PackageRow[]>([])
  const [destination, setDestination] = useState(pkg)
  const [identifier, setIdentifier] = useState('')
  const [edited, setEdited] = useState(false)
  const [basedOn, setBasedOn] = useState('')
  const [name, setName] = useState('')
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [more, setMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const [packageWarning, setPackageWarning] = useState('')

  // Only the kind's own level package - a case in <TC> TestCases (issue #114).
  useEffect(() => {
    api
      .packagesFor('verification', group)
      .then((answer) => {
        setPackages(answer.items)
        setPackageWarning(answer.warning)
      })
      .catch(() => setPackages([]))
  }, [group])

  const typesHere = kind.types.filter((choice) => choice.group === group)
  const chosen =
    typesHere.find((choice) => choice.name === typeName) ??
    typesHere.find((choice) => choice.name === kind.default_type) ??
    typesHere[0]
  const leveled = group !== 'case' || (chosen !== undefined && chosen.name !== kind.default_type)

  // Fields this kind has, in the registry's order, minus what is asked in the
  // dialog's own words. Resolved against the kind's list so each is drawn by its
  // FIELD KIND.
  const has = new Set(chosen?.fields ?? [])
  const available = kind.fields.filter(
    (entry) =>
      has.has(entry.name) &&
      !entry.derived &&
      !ASKED_ELSEWHERE.has(entry.name) &&
      (leveled || !LEVELED_ONLY.has(entry.name)),
  )
  const main = MAIN_FIELDS[group]
    .map((fieldName) => available.find((entry) => entry.name === fieldName))
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
  const optional = available.filter((entry) => !MAIN_FIELDS[group].includes(entry.name))

  // Issue #118: suggest an ID for wherever the definition will land - the
  // package, or with a parent the parent's package, which the server works out.
  // Re-asked on every change until the user types an ID of their own.
  useEffect(() => {
    let live = true
    const inside = fixedParent?.qualifiedName || parent
    api
      .suggestVerification({ group, package: inside ? '' : destination, parent: inside })
      .then((next) => {
        if (!live) return
        setBasedOn(next.based_on)
        if (!edited) setIdentifier(next.identifier)
      })
      .catch(() => live && setBasedOn(''))
    return () => {
      live = false
    }
  }, [group, destination, parent, fixedParent?.qualifiedName, edited])

  const parentGroup = PARENT_GROUP[group]
  // A suggested ID is not the user's work, so it alone does not guard the close.
  const dirty = Boolean((edited && identifier.trim()) || name.trim() || Object.values(values).some(Boolean))
  const release = useUnsavedGuard(dirty)

  const where = fixedParent?.qualifiedName || parent || destination

  const control = (entry: (typeof available)[number]) => (
    <FieldControl
      key={entry.name}
      schema={schema}
      kind={kind}
      field={entry}
      value={values[entry.name] ?? ''}
      onChange={(next) => setValues((prior) => ({ ...prior, [entry.name]: next }))}
      error={fieldErrors[entry.name]}
      multiline={LONG_TEXT_FIELDS.has(entry.name)}
    />
  )

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      // Issue #113: with a parent this is a def in a package plus a usage of it
      // in the parent. No package means the parent's own.
      const inside = fixedParent?.qualifiedName || parent
      const body: Record<string, unknown> = {
        package: inside ? '' : destination,
        ...(inside ? { parent: inside } : {}),
        identifier: identifier.trim(),
        type_name: chosen?.name,
      }
      if (name.trim()) body.name = name.trim()
      for (const entry of available) {
        const stated = values[entry.name]
        if (stated !== undefined && stated !== '') body[entry.name] = stated
      }
      const created = await api.create(kind.plural, body)
      toasts.succeeded(`${created.identifier} created`)
      release()
      onCreated(created.identifier)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  const ready = Boolean(identifier.trim() && chosen && where)

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        New {GROUP_LABEL[group].toLowerCase()}
        {fixedParent ? ` in ${fixedParent.label}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          {!fixedGroup && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={group}
              aria-label="Kind"
              onChange={(_event, next: VerificationGroup | null) => {
                if (!next) return
                setGroup(next)
                // Everything below belongs to the kind that asked for it. A
                // value kept across a change would be sent for a kind that
                // never declared it, and the create would fail on a field the
                // form no longer shows.
                setTypeName('')
                setParent('')
                setDestination(pkg)
                setValues({})
                setMore(false)
              }}
            >
              {GROUPS.map((entry) => (
                <ToggleButton key={entry} value={entry}>
                  {GROUP_LABEL[entry]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {typesHere.length > 1 && (
            <FormControl size="small">
              <InputLabel id="new-verification-type">Type</InputLabel>
              <Select
                labelId="new-verification-type"
                label="Type"
                value={chosen?.name ?? ''}
                onChange={(event) => {
                  setTypeName(event.target.value)
                  setValues({})
                }}
              >
                {typesHere.map((choice) => (
                  <MenuItem key={choice.name} value={choice.name}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {fixedParent ? (
            <TextField
              label="Inside"
              value={fixedParent.qualifiedName}
              size="small"
              helperText={fieldErrors.package || 'Included here; the definition goes in this element’s package'}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: MONOSPACE } } }}
            />
          ) : (
            <>
              {parentGroup && (
                // Only the kind above this one: a case in a plan, a plan in a
                // campaign. Optional, because a case may stand alone in a package.
                <ElementPicker
                  key={parentGroup}
                  schema={schema}
                  kind="verification"
                  where={(row) => {
                    const entry = row as VerificationRow
                    return entry.verification_kind === parentGroup && entry.is_definition
                  }}
                  valueFor={(row) => String(row.qualified_name)}
                  value={parent || null}
                  onChange={setParent}
                  label={`In ${GROUP_LABEL[parentGroup].toLowerCase()}`}
                  helperText="Optional. Coverage rolls up through the plan or campaign it sits in"
                />
              )}
              {!parent && (
                <ElementPicker
                  schema={schema}
                  options={packages.map((entry) => ({ value: entry.qualified_name }))}
                  value={destination || null}
                  onChange={setDestination}
                  label="Package"
                  required
                  error={Boolean(fieldErrors.package)}
                  helperText={fieldErrors.package || packageWarning || 'Where the declaration goes'}
                />
              )}
            </>
          )}

          <TextField
            label="ID"
            required
            value={identifier}
            onChange={(event) => {
              setIdentifier(event.target.value)
              setEdited(true)
            }}
            error={Boolean(fieldErrors.identifier)}
            helperText={
              fieldErrors.identifier || (basedOn ? `Suggested from ${basedOn}` : 'The short name, e.g. TC_STR_3')
            }
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />

          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'Optional; defaults to the ID'}
            size="small"
          />

          {main.map(control)}

          {optional.length > 0 && (
            <>
              <Button size="small" onClick={() => setMore((open) => !open)} sx={{ alignSelf: 'flex-start' }}>
                {more ? 'Fewer fields' : `More fields (${optional.length})`}
              </Button>
              {more && optional.map(control)}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
